import { useEffect, useState } from "react";
import { Loader2, Calendar, Mail, CreditCard, MessageSquare, Building2, CalendarClock, Plug, CheckCircle2, ShieldCheck, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CAT_META = {
  calendar: { label: "Calendars & Booking", icon: Calendar },
  booking: { label: "Booking Platforms", icon: CalendarClock },
  email: { label: "Email", icon: Mail },
  payment: { label: "Payments", icon: CreditCard },
  messaging: { label: "Messaging", icon: MessageSquare },
  crm: { label: "CRM", icon: Building2 },
};
const CAT_ICON = { calendar: Calendar, booking: CalendarClock, email: Mail, payment: CreditCard, messaging: MessageSquare, crm: Building2 };
const ORDER = ["calendar", "booking", "email", "payment", "messaging", "crm"];

export default function Integrations() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/integrations").then((r) => setItems(r.data.integrations)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!items) return <div className="flex items-center gap-2 text-slate-400 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading integrations…</div>;

  const connect = async (it) => {
    setBusy(it.key);
    try {
      const r = await api.post("/integrations/connect", { provider: it.key });
      if (r.data.status === "connected") { toast.success(r.data.message); load(); }
      else toast.info(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not connect"); }
    finally { setBusy(""); }
  };
  const disconnect = async (it) => {
    setBusy(it.key);
    try { await api.post("/integrations/disconnect", { provider: it.key }); load(); toast.success("Disconnected"); }
    catch (e) { toast.error("Could not disconnect"); }
    finally { setBusy(""); }
  };

  const Card = ({ it }) => {
    const Icon = CAT_ICON[it.category] || Plug;
    return (
      <div data-testid={`integration-${it.key}`} className={`rounded-xl border bg-white p-4 flex flex-col ${it.is_default ? "border-blue-300 ring-1 ring-blue-100" : "border-slate-200"}`}>
        <div className="flex items-start justify-between">
          <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600"><Icon className="h-4.5 w-4.5" /></div>
          {it.coming_soon
            ? <span className="rounded-full bg-slate-100 text-slate-500 text-[11px] font-semibold px-2 py-0.5">Coming soon</span>
            : <span data-testid={`status-${it.key}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${it.connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{it.connected ? <><CheckCircle2 className="h-3 w-3" /> Connected</> : "Not connected"}</span>}
        </div>
        <p className="mt-3 font-semibold text-slate-900 text-sm flex items-center gap-1.5">{it.name}{it.is_default && <Star className="h-3.5 w-3.5 text-blue-500 fill-blue-500" />}{it.optional && <span className="text-[10px] text-slate-400 font-normal">(optional)</span>}</p>
        {it.is_default && <p className="text-[11px] text-blue-600 mt-0.5">Your default booking system</p>}
        {!it.coming_soon && (it.auth === "oauth") && <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400"><ShieldCheck className="h-3 w-3" /> Secure OAuth</p>}
        <div className="mt-auto pt-3">
          {it.coming_soon ? (
            <Button data-testid={`connect-${it.key}`} variant="outline" onClick={() => connect(it)} disabled={busy === it.key} className="w-full h-8 text-xs text-slate-500">Notify me</Button>
          ) : it.connected && !it.builtin ? (
            <Button data-testid={`disconnect-${it.key}`} variant="outline" onClick={() => disconnect(it)} disabled={busy === it.key} className="w-full h-8 text-xs">{busy === it.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Disconnect"}</Button>
          ) : it.connected && it.builtin ? (
            <Button disabled variant="outline" className="w-full h-8 text-xs text-emerald-600">Active</Button>
          ) : (
            <Button data-testid={`connect-${it.key}`} onClick={() => connect(it)} disabled={busy === it.key} className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700 gap-1">{busy === it.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plug className="h-3 w-3" /> Connect</>}</Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="integrations-view">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Integrations</h1>
      <p className="text-slate-500 mt-1">Connect OperaMind to the tools you already use. Secure OAuth — you never share a password with us.</p>
      {ORDER.map((cat) => {
        const group = items.filter((i) => i.category === cat);
        if (!group.length) return null;
        const Meta = CAT_META[cat];
        return (
          <div key={cat} className="mt-8">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Meta.icon className="h-3.5 w-3.5" /> {Meta.label}</h2>
            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">{group.map((it) => <Card key={it.key} it={it} />)}</div>
          </div>
        );
      })}
    </div>
  );
}
